import './mail-transfer-actions.ajscss';

import { AgentDesktopRouter } from '@agent/agent-interface/agent-desktop-router';
import { AbstractEpticaController } from '@agent/agent-interface/contact-view/mail/abstract-mail-process';
import { IEpticaDeprecatedService } from '@agent/core/channel-digit/mail/eptica/eptica-deprecated.service';
import { EpticaData } from '@agent/core/channel-digit/mail/eptica/models/eptica-data';
import {
  HOME_PAGE_NAME,
  IEpticaRequestFrame,
  REQUEST_PAGE_NAME,
} from '@agent/core/channel-digit/mail/eptica/models/eptica.request.interfaces';
import { IContactDeprecatedService } from '@agent/core/contact/contact-deprecated.service';
import { ITransfer, TransferKey, TransferTarget } from '@agent/core/contact/transfer/models/transfer';
import { ITransferDeprecatedService } from '@agent/core/contact/transfer/transfer-deprecated.service';
import { IKnowledgeBaseService } from '@agent/core/knowledge-base/knowledge-base.service';
import { SessionDeprecatedService } from '@commons/core/auth/session-deprecated.service';
import { ToastDeprecatedService } from '@commons/core/notification/toast-deprecated.service';
import { IEpticaExtractAttachmentFrom } from '@commons/history/history-mail/models/eptica-extract';
import { IContact } from '@commons/models/contact';
import { destroy, ng1Destroy, RxUtil } from '@commons/util/utilities/rx';
import { IComponentController, IComponentOptions, ILogService, IPromise, IQService, isString } from 'angular';

import {
  Booking,
  BookingFacade,
  BookingMailFacade,
  Configuration,
  ChannelType,
  isNotNullOrUndefined,
  ProfileFacade,
  TranslateService,
  Skill,
} from '@contact/upgrade';

import templateUrl from './mail-transfer-actions.template.html';
import { KnowledgeBaseOwiService } from '@agent/agent-interface/helpzone/knowledge-base/owi/services/knowledge-base-OWI.service';

class MailTransferActionsCtrl extends AbstractEpticaController<IEpticaRequestFrame> implements IComponentController {
  override responseId: string;
  private enableValidateBtn = true;
  private requestPromise: IPromise<IEpticaRequestFrame>;
  private transferKey: TransferKey;
  private contact: Booking;
  private profileId: string;

  private isKnowledgeBaseFrameEnabled = this.configuration.isKnowledgeBaseFrameEnabled ?? false;

  constructor(
    override readonly $log: ILogService,
    override readonly $q: IQService,
    private readonly transferDeprecatedService: ITransferDeprecatedService,
    override readonly contactDeprecatedService: IContactDeprecatedService,
    private readonly epticaDeprecatedService: IEpticaDeprecatedService,
    override readonly toastDeprecatedService: ToastDeprecatedService,
    private readonly knowledgeBaseService: IKnowledgeBaseService,
    private readonly sessionDeprecatedService: SessionDeprecatedService,
    private readonly contactFacade: BookingFacade,
    private readonly bookingMailFacade: BookingMailFacade,
    private readonly profileFacade: ProfileFacade,
    override readonly router: AgentDesktopRouter,
    private readonly knowledgeBaseOwiService: KnowledgeBaseOwiService,
    private readonly configuration: Configuration,
    override readonly translateService: TranslateService
  ) {
    super($log, $q, contactDeprecatedService, toastDeprecatedService, router, translateService);
  }

  get canValidate(): boolean {
    return this.enableValidateBtn && this.transferDeprecatedService.canValidateTransfer(this.transferKey, ChannelType.MAIL);
  }

  validate(): void {
    this.enableValidateBtn = false;
    this.transferDeprecatedService.getTransfer(this.transferKey).map((transfer: ITransfer) => {
      if (this.sessionDeprecatedService.hasPermission('CSR::bad_request_routing:report') && transfer.sendKnowledgeBaseNotification) {
        // Notify the knowledge Base that a routing error appeared

        //since transferKey is an array of 1 string, and mediaExternalId needs to be a string
        const [mediaExternalId] = this.transferKey;
        if (this.isKnowledgeBaseFrameEnabled) {
          this.knowledgeBaseService.notifySkillsKO(mediaExternalId.toString()).subscribe();
        } else {
          this.knowledgeBaseOwiService.sendKoSkills(mediaExternalId.toString()).subscribe();
        }
      }
      switch (transfer.targetType) {
        case TransferTarget.TEAM:
          this.forwardToTeam(transfer);
          break;
        case TransferTarget.SKILL:
          this.forwardToSkills(transfer);
          break;
        case TransferTarget.AGENT:
          this.forwardToAgent(transfer);
          break;
        case TransferTarget.CORRESPONDENT:
          this.forwardToDirectory(transfer);
          break;
        case TransferTarget.COMPOSITE:
          this.forwardToComposite(transfer);
          break;
        case TransferTarget.FREE_NUMBER:
          this.forwardToFreeMail(transfer);
          break;
      }
    });
  }

  private forwardToSkills(transfer: ITransfer): IPromise<IContact> {
    return this.mainPromise().then(request =>
      RxUtil.toPromise(
        this.epticaDeprecatedService.forwardRequestToSkill(request.getId(), {
          source: this.contact.skills,
          target: transfer.target as Skill[],
          comment: transfer.comment,
          attachments: this.epticaDeprecatedService
            .getAttachmentFrom(`${request.getId()}`, IEpticaExtractAttachmentFrom.TRANSFER)
            .map(epticaAtt => epticaAtt.file),
        }),
        this.$q
      )
        .then(() => this.endContact(this.responseId))
        .finally(() => request.finish())
    );
  }

  private forwardToTeam(transfer: ITransfer): IPromise<IContact> {
    return this.mainPromise().then(request =>
      RxUtil.toPromise(
        this.epticaDeprecatedService.forwardRequestToTeam(request.getId(), {
          target: transfer.target.id,
          comment: transfer.comment,
          sourceSkills: this.contact.skills,
          targetSkills: transfer.target.skills,
          attachments: this.epticaDeprecatedService
            .getAttachmentFrom(`${request.getId()}`, IEpticaExtractAttachmentFrom.TRANSFER)
            .map(epticaAtt => epticaAtt.file),
        }),
        this.$q
      )
        .then(() => this.endContact(this.responseId))
        .finally(() => request.finish())
    );
  }

  private forwardToDirectory(transfer: ITransfer): IPromise<IContact> {
    return this.forwardToMail(transfer.target.email, transfer.comment);
  }

  private forwardToFreeMail(transfer: ITransfer): IPromise<void> {
    const { target, comment } = transfer;
    return this.mainPromise()
      .then(request => {
        let attachments: File[] | undefined = this.epticaDeprecatedService
          .getAttachmentFrom(`${request.getId()}`, IEpticaExtractAttachmentFrom.TRANSFER)
          .map(epticaAtt => epticaAtt.file);
        request.finish();
        return { requestId: request.getId(), attachments };
      })
      .then(({ requestId, attachments }) => {
        this.bookingMailFacade.forwardToMail(this.responseId, {
          param: {
            target,
            comment,
            includedMessages: this.transferDeprecatedService.getIncludedMessagesId([`${requestId}`]),
          },
          attachments,
        });
      });
  }

  private forwardToMail(email: string, comment: string): IPromise<IContact> {
    return this.mainPromise().then(request =>
      RxUtil.toPromise(
        this.epticaDeprecatedService.forwardRequestToMail(request.getId(), {
          target: email,
          comment,
          attachments: this.epticaDeprecatedService
            .getAttachmentFrom(`${request.getId()}`, IEpticaExtractAttachmentFrom.TRANSFER)
            .map(epticaAtt => epticaAtt.file),
          includedMessages: this.transferDeprecatedService.getIncludedMessagesId([`${request.getId()}`]),
        }),
        this.$q
      )
        .then(() => this.endContact(this.responseId))
        .finally(() => request.finish())
    );
  }

  private forwardToAgent(transfer: ITransfer): IPromise<IContact> {
    return this.mainPromise().then(request =>
      RxUtil.toPromise(
        this.epticaDeprecatedService.forwardRequestToAgent(request.getId(), {
          target: transfer.target.uuid,
          comment: transfer.comment,
          attachments: this.epticaDeprecatedService
            .getAttachmentFrom(`${request.getId()}`, IEpticaExtractAttachmentFrom.TRANSFER)
            .map(epticaAtt => epticaAtt.file),
        }),
        this.$q
      )
        .then(() => this.endContact(this.responseId))
        .finally(() => request.finish())
    );
  }

  forwardToComposite(transfer: ITransfer): IPromise<IContact | void> {
    const { target } = transfer;
    if (isString(target)) {
      return this.forwardToFreeMail({
        ...transfer,
        target,
      });
    }
    switch (target.type) {
      case 'contact':
      case 'directory':
        return this.forwardToFreeMail({
          ...transfer,
          target: target.email,
        });
      case 'agent':
        return this.forwardToAgent({
          ...transfer,
          target: {
            uuid: target.id,
          },
        });
      default:
        this.$log.error('MailTransferActionsCtrl: transfertComposite: unknown target');
        return this.$q.reject();
    }
  }

  cancel(): void {
    this.transferDeprecatedService.cancelTransfer(this.transferKey).then(() => {
      this.enableValidateBtn = true;
      this.mainPromise().then(request => {
        request.toggleAnswerPanel(true);
        request.toggleDelegatePanel(false);
      });
      this.router.navigateFromLastChild(['..']);
    });
  }

  mainPromise(): IPromise<IEpticaRequestFrame> {
    return this.requestPromise;
  }

  override $onInit(): void {
    super.$onInit();
    const contact$ = this.contactFacade.selected$.pipe(isNotNullOrUndefined());
    // selected contact
    contact$.pipe(ng1Destroy(this)).subscribe(contact => (this.contact = contact));

    this.requestPromise = this.contactDeprecatedService.getContact(this.responseId).then(contact =>
      this.epticaDeprecatedService.loadRequest({
        requestId: contact.epticaRequestId || 0,
        responseId: this.responseId,
      })
    );

    this.mainPromise().then(request => {
      request.toggleDelegatePanel(true);
      // compute transfer key
      this.transferKey = [request.getId()];
      // set default target
      this.transferDeprecatedService.getTransfer(this.transferKey).map((transfer: ITransfer) => {
        transfer.targetType = TransferTarget.COMPOSITE;
      });
    });

    this.contactFacade
      .getById(this.responseId)
      .pipe(isNotNullOrUndefined(), destroy(this))
      .subscribe(contact => (this.contact = contact));

    this.profileFacade.id$.pipe(ng1Destroy(this)).subscribe(id => (this.profileId = id));
  }

  checkEpticaDataAfterReload(request: IEpticaRequestFrame, epticaData: EpticaData): IPromise<EpticaData> {
    if (epticaData.pageName === HOME_PAGE_NAME) {
      this.endContact(this.responseId).finally(() => request.finish());
    } else if (epticaData.pageName === REQUEST_PAGE_NAME) {
      // Show the request
      request.show();
    } else {
      this.contactDeprecatedService.cancel(this.responseId).finally(() => this.mainPromise().then(r => r.finish()));
    }
    return this.$q.resolve(epticaData);
  }

  /**
   * End the contact normally
   */
  protected override endContact(responseId: string): IPromise<IContact> {
    return this.contactDeprecatedService.getContact(responseId).then(() => this.contactDeprecatedService.close(this.responseId, true));
  }
}

export class MailTransferActions implements IComponentOptions {
  templateUrl = templateUrl;
  bindings = {
    responseId: '<',
  } as { [binding: string]: string };
  controller = [
    '$log',
    '$q',
    'transferDeprecatedService',
    'contactDeprecatedService',
    'epticaDeprecatedService',
    'toastDeprecatedService',
    'knowledgeBaseService',
    'sessionDeprecatedService',
    'contactFacade',
    'bookingMailFacade',
    'profileFacade',
    'agentDesktopRouter',
    'knowledgeBaseOwiService',
    'configuration',
    'translateService',
    MailTransferActionsCtrl,
  ];
}

export default 'contactMailTransferActions';
